import { sanitizeFileName, type DownloadFilesResult, type ZipFileEntry } from './downloadFilesAsZip';
import {
  createUrlResolver,
  openResumableEntry,
  reconnectTracker,
  sizeOfEntry,
  type BulkFetchOptions,
} from './resumableDownload';

/**
 * 一括ダウンロードの「フォルダに保存」版。
 *
 * zip は 1 本のファイルなので、途中で止まると書きかけの zip は破棄され最初からやり直しになる。
 * こちらは選ばれたフォルダの中に `<ダウンロード名>/` を作り、フォルダ構成どおり 1 ファイルずつ
 * 直接保存する。同じ保存先でもう一度実行すると、保存済みで同じサイズのファイルは飛ばすので、
 * 止まった所から再開できる。対応ブラウザは Chrome / Edge（`showDirectoryPicker`）。
 */

/** 同時に保存するファイル数 */
const CONCURRENCY = 3;

interface WindowWithDirectoryPicker {
  showDirectoryPicker?: (options?: {
    id?: string;
    mode?: 'read' | 'readwrite';
  }) => Promise<FileSystemDirectoryHandle>;
}

export const canSaveToDirectory = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as unknown as WindowWithDirectoryPicker).showDirectoryPicker === 'function';

/**
 * 保存先のフォルダを選ばせる。キャンセル時は null。
 * ユーザー操作（クリック）の直後に呼ぶこと（間に await を挟むと拒否される）。
 */
export async function pickSaveDirectory(): Promise<FileSystemDirectoryHandle | null> {
  const picker = (window as unknown as WindowWithDirectoryPicker).showDirectoryPicker;
  if (!picker) return null;
  try {
    // id を固定すると、次回も同じフォルダから選び始められる（再開しやすい）
    return await picker({ id: 'soremo-bulk-download', mode: 'readwrite' });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    throw error;
  }
}

export interface SaveToDirectoryResult extends DownloadFilesResult {
  /** 保存済み（同じサイズのファイルがすでにある）で飛ばした件数。succeeded に含む */
  skipped: number;
}

const segmentName = (segment: string): string =>
  segment === '.' || segment === '..' ? '_' : sanitizeFileName(segment);

/** 同じパスが複数あるときは 2 件目以降に「 (2)」などを付ける（順序が同じなら再開時も同じ名前になる） */
const uniquePaths = (entries: ZipFileEntry[]): string[] => {
  const used = new Set<string>();
  return entries.map((entry) => {
    const parts = entry.path.split('/').filter(Boolean).map(segmentName);
    const base = parts.join('/') || 'download';
    let path = base;
    for (let n = 2; used.has(path.toLowerCase()); n += 1) {
      const dot = base.lastIndexOf('.');
      const slash = base.lastIndexOf('/');
      path = dot > slash + 1 ? `${base.slice(0, dot)} (${n})${base.slice(dot)}` : `${base} (${n})`;
    }
    used.add(path.toLowerCase());
    return path;
  });
};

const existingSize = async (dir: FileSystemDirectoryHandle, name: string): Promise<number | null> => {
  try {
    const handle = await dir.getFileHandle(name);
    return (await handle.getFile()).size;
  } catch {
    return null;
  }
};

/**
 * entries を root/<folderName>/ 以下へ保存する。
 * 保存に失敗したファイルは書きかけを残さず（保存先の一時ファイルは破棄される）、次のファイルへ進む。
 */
export async function saveFilesToDirectory(
  entries: ZipFileEntry[],
  root: FileSystemDirectoryHandle,
  folderName: string,
  options?: BulkFetchOptions,
): Promise<SaveToDirectoryResult> {
  const total = entries.length;
  const succeededFileIds: string[] = [];
  let succeeded = 0;
  let failed = 0;
  let skipped = 0;
  let processed = 0;

  const resolver = createUrlResolver(options?.refreshEntries);
  const onReconnect = reconnectTracker(options?.onReconnectingChange);
  const base = await root.getDirectoryHandle(segmentName(folderName), { create: true });

  const dirs = new Map<string, Promise<FileSystemDirectoryHandle>>([['', Promise.resolve(base)]]);
  const dirOf = (path: string): Promise<FileSystemDirectoryHandle> => {
    const cached = dirs.get(path);
    if (cached) return cached;
    const slash = path.lastIndexOf('/');
    const created = dirOf(slash < 0 ? '' : path.slice(0, slash)).then((parent) =>
      parent.getDirectoryHandle(path.slice(slash + 1), { create: true }),
    );
    dirs.set(path, created);
    return created;
  };

  const save = async (entry: ZipFileEntry, path: string): Promise<'saved' | 'skipped' | 'failed'> => {
    const slash = path.lastIndexOf('/');
    const name = path.slice(slash + 1);
    const dir = await dirOf(slash < 0 ? '' : path.slice(0, slash));

    const expected = sizeOfEntry(entry);
    const existing = await existingSize(dir, name);
    if (expected !== null && existing === expected) return 'skipped';

    const opened = await openResumableEntry(entry, resolver, { onReconnect });
    if (!opened) return 'failed';
    const handle = await dir.getFileHandle(name, { create: true });
    try {
      // 失敗すると pipeTo が書き込みを中止し、書きかけの内容は反映されない
      await opened.stream.pipeTo(await handle.createWritable());
      return 'saved';
    } catch {
      // 今回新しく作った空ファイルは残さない（前回の不完全なファイルは次回また取り直す）
      if (existing === null) await dir.removeEntry(name).catch(() => undefined);
      return 'failed';
    }
  };

  const paths = uniquePaths(entries);
  let next = 0;
  const worker = async () => {
    while (next < entries.length) {
      const index = next;
      next += 1;
      const entry = entries[index];
      let outcome: 'saved' | 'skipped' | 'failed';
      try {
        outcome = await save(entry, paths[index]);
      } catch {
        outcome = 'failed';
      }
      processed += 1;
      if (outcome === 'failed') {
        failed += 1;
      } else {
        succeeded += 1;
        if (outcome === 'skipped') skipped += 1;
        else if ('file' in entry && entry.file.id) succeededFileIds.push(entry.file.id);
      }
      options?.onProgress?.(processed, total);
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
  return { total, succeeded, failed, skipped, succeededFileIds };
}
